import { useEffect, useRef } from "react";

import { UIView, useCurrentStateAndParams, useRouter } from "@uirouter/react";
import startsWith from "lodash/startsWith";
import { ErrorBoundary } from "react-error-boundary";

import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { InfoBanner } from "app/components/React/PlaResearch/InfoBanner";
import { KeywordAnalysisSubNav } from "app/pages/keyword-research/keyword-analysis/common-components/KeywordAnalysisSubNav";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { DEFAULT_PAGE_LAYOUT_CLASS_NAME } from "app/UtilitiesAndConstants/Constants/css";

import { KEYWORD_GROUP_PREFIX, keywordGroupsTransforms, useAllKeywordGroups } from "app/services/keyword-groups";

interface HasId {
    [key: string]: any;
    id: string;
}

export const getGroupById = (id: string, groups): HasId => {
    const groupId = id.replace(new RegExp("^[" + KEYWORD_GROUP_PREFIX + "]"), "");
    return groups.flat(1).find((item) => {
        return groupId.toLowerCase() === item.id.toLowerCase() || groupId.toLowerCase() === item.text.toLowerCase();
    });
};

export const normalizeKeyword = (searchTerm: any) => {
    if (typeof searchTerm === "string") {
        searchTerm = searchTerm.trim();
        if (!startsWith(searchTerm, "*")) {
            searchTerm = searchTerm.toLowerCase();
        }
    }
    return searchTerm;
};

const determineMode = (searchTerm: string, isTopic: boolean, isTerm: boolean): QueryBarItemMode => {
    if (isTopic) {
        return "topic";
    }
    if (isTerm) {
        return "searchTerm";
    }
    if (!startsWith(searchTerm, KEYWORD_GROUP_PREFIX)) {
        return "keyword";
    }
    return "keywordGroup";
};

interface Params {
    keyword: string;
    isTerm: string;
    isTopic: string;
}

interface StateParams {
    params: Params;
    state: IRouterState;
}

export const KeywordAnalysisModuleFrame = ({ showCompactCreateListCTAButton = true, shouldLogErrorFallback }) => {
    const { params, state }: StateParams = useCurrentStateAndParams();
    const handler = useRef(null);
    const router = useRouter();
    const searchTerm = params.keyword || "";
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();
    const isTerm = params.isTerm === "true";
    const isTopic = params.isTopic === "true";

    const { userGroups, sharedGroups, isLoading } = useAllKeywordGroups();
    const keywordsGroupsMyListsForDropDown = keywordGroupsTransforms.toDropDown(userGroups).map((item) => ({
        ...item,
        shareable: true,
        editable: true,
        deletable: true,
    }));

    const keywordsGroupsSharedListsForDropDown = keywordGroupsTransforms.toDropDown(sharedGroups).map((item) => ({
        ...item,
        shareable: false,
        editable: false,
        deletable: false,
    }));

    useEffect(() => {
        const normalized = normalizeKeyword(searchTerm);
        if (normalized !== searchTerm) {
            router.stateService.go(state, { keyword: normalized });
        }
        handler.current = router.transitionService.onStart({}, (transition) => {
            const searchTerm = transition.params().keyword;
            const normalized = normalizeKeyword(searchTerm);
            if (searchTerm && normalized && normalized !== searchTerm) {
                return transition.router.stateService.target(state, { keyword: normalized });
            }
        });
        return () => {
            if (typeof handler.current === "function") {
                handler.current();
            }
        };
    }, []);

    const keywordsGroupsForDropDown = [];
    keywordsGroupsForDropDown.push(keywordsGroupsMyListsForDropDown);
    keywordsGroupsForDropDown.push(keywordsGroupsSharedListsForDropDown);
    const mode = determineMode(searchTerm, isTopic, isTerm);

    const selectedKeywordsGroupId =
        mode === "keywordGroup" ? getGroupById(searchTerm, keywordsGroupsForDropDown)?.id : undefined;

    const pageLayoutClassName = state.pageLayoutClassName ?? DEFAULT_PAGE_LAYOUT_CLASS_NAME;
    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    shouldLogErrorFallback={shouldLogErrorFallback}
                />
            )}
            resetKeys={[state, params]}
        >
            <section
                className="keyword-analysis-search sw-layout-no-scroll-container"
                style={{ height: "100%", display: "flex" }}
            >
                <span
                    className="u-flex-row u-flex-column"
                    style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                >
                    <SubNavWrapper
                        className="sw-layout-subnav"
                        {...subNavWrapperProps}
                    >
                        <KeywordAnalysisSubNav
                            categories={[]}
                            customCategories={keywordsGroupsForDropDown}
                            selectedCat={selectedKeywordsGroupId}
                            trackName={"Header/Keyword Group Filter"}
                            trackContext={"keyword group"}
                            removeItemTooltip={{
                                title: "Are you sure you want to delete this group?",
                                buttonOk: "Delete",
                                buttonCancel: "Cancel",
                            }}
                            placeholderItem={{
                                text: "Choose Keyword Group",
                                icon: "sw-icon-folder",
                            }}
                            mode={mode}
                            searchTerm={searchTerm}
                            showAutoCreateListCTA
                            showCompactCreateListCTAButton={showCompactCreateListCTAButton}
                            isLoading={isLoading}
                            showCompareButton={state.showCompareButton}
                        />
                    </SubNavWrapper>
                    <InfoBanner
                        displayInPage="paidsearch_keywordAnalysis_plaResearch"
                        userDataBannerKey="userDataBannerKey"
                        text="keyword.analysis.plaResearch.page.toolTip.text"
                        title="keyword.analysis.plaResearch.page.toolTip.title"
                    />
                    <PageContentWrapper
                        className="sw-layout-research sw-layout-scrollable-element use-sticky-css-rendering"
                        {...pageContentWrapperProps}
                    >
                        <div className={`sw-page-websiteAnalysis ${pageLayoutClassName}`}>
                            <ErrorBoundary
                                FallbackComponent={(props) => (
                                    <ErrorFallback
                                        {...props}
                                        shouldLogErrorFallback={shouldLogErrorFallback}
                                    />
                                )}
                                resetKeys={[state, params]}
                            >
                                <UIView />
                            </ErrorBoundary>
                        </div>
                    </PageContentWrapper>
                </span>
            </section>
        </ErrorBoundary>
    );
};
